import { useEffect, useRef } from "react";

interface CanvasGridProps {
  mx: number;
  my: number;
  reducedMotion?: boolean;
  rippleTrigger?: { x: number; y: number; id: number } | null;
}

interface Ripple {
  x: number;
  y: number;
  t0: number;
}

export function CanvasGrid({ mx, my, reducedMotion = false, rippleTrigger = null }: CanvasGridProps) {
  const ref = useRef<HTMLCanvasElement>(null);
  const mRef = useRef({ x: mx, y: my });
  const ripplesRef = useRef<Ripple[]>([]);

  useEffect(() => {
    mRef.current = { x: mx, y: my };
  }, [mx, my]);

  useEffect(() => {
    if (!rippleTrigger || reducedMotion) return;
    const canvas = ref.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const x = ((rippleTrigger.x - rect.left) / rect.width) * canvas.width;
    const y = ((rippleTrigger.y - rect.top) / rect.height) * canvas.height;
    ripplesRef.current.push({ x, y, t0: performance.now() });
    if (ripplesRef.current.length > 16) {
      ripplesRef.current.shift();
    }
  }, [rippleTrigger, reducedMotion]);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const scale = 1.4;
    const spacing = 48;
    const resize = () => {
      const parent = canvas.parentElement;
      const w = parent?.offsetWidth ?? canvas.offsetWidth;
      const h = parent?.offsetHeight ?? canvas.offsetHeight;
      canvas.width = Math.max(1, w * scale);
      canvas.height = Math.max(1, h * scale);
    };
    resize();
    const observer = new ResizeObserver(resize);
    if (canvas.parentElement) observer.observe(canvas.parentElement);
    window.addEventListener("resize", resize);

    const INFLUENCE_RADIUS = 390;
    const FOLLOW_STRENGTH = 0.22;
    let animId: number;

    const draw = () => {
      const now = performance.now();
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const drawW = canvas.width;
      const drawH = canvas.height;
      const gap = spacing * scale;
      const COLS = Math.max(8, Math.round(drawW / gap) + 1);
      const ROWS = Math.max(6, Math.round(drawH / gap) + 1);
      const cw = drawW / (COLS - 1);
      const rh = drawH / (ROWS - 1);
      const centerX = drawW / 2;
      const centerY = drawH / 2;
      const maxRadius = Math.sqrt(centerX * centerX + centerY * centerY);
      const { x: mX, y: mY } = mRef.current;
      const rect = canvas.getBoundingClientRect();
      const mouseInCanvasX = mX - rect.left;
      const mouseInCanvasY = mY - rect.top;
      const mouseBufX = (mouseInCanvasX / rect.width) * drawW;
      const mouseBufY = (mouseInCanvasY / rect.height) * drawH;

      for (let r = 0; r < ROWS; r++) {
        for (let c = 0; c < COLS; c++) {
          const x = c * cw;
          const y = r * rh;
          const dx = mouseBufX - x;
          const dy = mouseBufY - y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          const inf = Math.max(0, 1 - dist / INFLUENCE_RADIUS);
          const distFromCenter = Math.sqrt((x - centerX) ** 2 + (y - centerY) ** 2);
          const edgeFalloff = Math.max(0, 1 - distFromCenter / maxRadius);
          const alpha = Math.min(0.92, (0.09 + inf * 0.8) * edgeFalloff);
          const radius = 2.8 + inf * 5.8;
          const drift = reducedMotion ? 0 : Math.sin(now * 0.0016 + c * 0.32 + r * 0.19) * 1.4;
          const ox = dx * inf * FOLLOW_STRENGTH + drift;
          const oy = dy * inf * FOLLOW_STRENGTH + (reducedMotion ? 0 : Math.cos(now * 0.0014 + c * 0.2 + r * 0.23) * 1.2);
          ctx.beginPath();
          ctx.arc(x + ox, y + oy, radius, 0, Math.PI * 2);
          ctx.fillStyle =
            inf > 0.78
              ? `rgba(224,242,254,${Math.min(1, alpha * 1.08)})`
              : inf > 0.45
              ? `rgba(217,70,239,${alpha * 0.82})`
              : `rgba(14,165,233,${alpha})`;
          ctx.fill();
        }
      }

      if (!reducedMotion && ripplesRef.current.length) {
        const RIPPLE_LIFETIME = 850;
        const RIPPLE_SPEED = 0.52;
        ripplesRef.current = ripplesRef.current.filter((ripple) => now - ripple.t0 < RIPPLE_LIFETIME);
        for (const ripple of ripplesRef.current) {
          const age = now - ripple.t0;
          const p = age / RIPPLE_LIFETIME;
          const base = 12 + age * RIPPLE_SPEED;
          const fade = 1 - p;

          ctx.lineWidth = 2.2;
          ctx.strokeStyle = `rgba(14,165,233,${0.42 * fade})`;
          ctx.beginPath();
          ctx.arc(ripple.x, ripple.y, base, 0, Math.PI * 2);
          ctx.stroke();

          ctx.lineWidth = 1.7;
          ctx.strokeStyle = `rgba(217,70,239,${0.32 * fade})`;
          ctx.beginPath();
          ctx.arc(ripple.x, ripple.y, base * 0.72, 0, Math.PI * 2);
          ctx.stroke();
        }
      }
      animId = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", resize);
      cancelAnimationFrame(animId);
    };
  }, []);

  return (
    <canvas
      ref={ref}
      className="absolute inset-0 w-full h-full"
      style={{
        pointerEvents: "none",
        width: "140%",
        height: "140%",
        left: "-20%",
        top: "-20%",
      }}
    />
  );
}
